import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CONFIG from '../config';

// ====================================================================
// EefcPage                                             v22  Stage S5
// --------------------------------------------------------------------
// Money LEAVING an EEFC ledger. Two ways out:
//
//   To INR          — sold for rupees, at spot, on a forward, or both
//   Cross currency  — buys another currency at the same bank
//
// --------------------------------------------------------------------
// WHY THIS IS ITS OWN PAGE, NOT PART OF REALISATION
// --------------------------------------------------------------------
// A conversion is not a realisation. By the time money is sitting in an
// EEFC ledger the bill is already settled — fc_realised and
// fc_discounted moved when the money was parked. The customer has paid.
// The only thing outstanding is that the currency has not been turned
// into rupees.
//
// Nothing on this page touches an invoice, an order or a realisation.
//
// --------------------------------------------------------------------
// WHAT THE PAGE SHOWS
// --------------------------------------------------------------------
//   the ledgers that hold money
//   their parcels, OLDEST FIRST, each with the invoice behind it
//   which parcels an amount would take, and what each one earns,
//     BEFORE anything is saved
//   the conversions already made, with undo where it is still allowed
//
// Money always leaves oldest parcel first (rule R3). Nobody chooses the
// parcel, because choosing the parcel would mean choosing which invoice
// receives the profit.
//
// The gain on each parcel splits in two (rule R4):
//
//   export gain  = (rate on arrival - cost rate) x FC
//   holding gain = (exit rate - rate on arrival) x FC
//
// Export gain is what the export itself earned. Holding gain is what
// waiting earned on top. Keeping them apart is the only way to answer
// "did holding the money actually pay?".
// ====================================================================

const fmt = (n, dp = 2) =>
  n === null || n === undefined || n === '' || isNaN(Number(n))
    ? '—'
    : Number(n).toLocaleString('en-IN', { minimumFractionDigits: dp, maximumFractionDigits: dp });

const fmt0 = (n) =>
  n === null || n === undefined || n === '' || isNaN(Number(n))
    ? '—'
    : Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 });

const rate4 = (n) => (Number(n) > 0 ? Number(n).toFixed(4) : '—');

const num = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

const ccyCode   = (c) => (c && c.length === 6 ? c.slice(0, 3) : (c || ''));
const pairLabel = (c) =>
  c && c.length === 6 ? `${c.slice(0, 3)}/${c.slice(3)}` : (c || '—');

const today = () => new Date().toISOString().slice(0, 10);

const serverMessage = (err, fallback) => {
  const d = err?.response?.data;
  if (typeof d === 'string' && d.trim()) return d;
  if (d && typeof d.title === 'string') return d.title;
  if (d && typeof d.message === 'string') return d.message;
  return fallback;
};

const readForwards = (data) => {
  if (Array.isArray(data)) return { list: data, reason: null };
  return { list: data?.forwards || [], reason: data?.reason || null };
};

const TOL = 0.005;

// --------------------------------------------------------------------
// FIFO, worked out here so the screen can show it as the amount is
// typed. The server works it out again inside its own transaction —
// this is a preview, never the decision.
// --------------------------------------------------------------------
const planFifo = (parcels, wanted) => {
  const takes = [];
  if (!(wanted > 0)) return { ok: false, reason: null, takes };

  const available = (parcels || [])
    .reduce((s, p) => s + num(p.fcRemaining), 0);

  if (wanted > available + TOL) {
    return {
      ok: false,
      reason: `This ledger holds ${fmt(available)}, so ${fmt(wanted)} cannot be converted from it.`,
      takes
    };
  }

  let remaining = wanted;
  for (const p of parcels || []) {
    if (remaining <= TOL) break;
    const open = num(p.fcRemaining);
    if (open <= TOL) continue;

    const take = Math.min(remaining, open);
    takes.push({ ...p, fcTaken: take, fcRemainingAfter: open - take });
    remaining -= take;
  }

  return { ok: true, reason: null, takes };
};

const gainsFor = (takes, exitRate) => {
  let gain = 0, exportGain = 0, holding = 0;
  takes.forEach(t => {
    const fc    = num(t.fcTaken);
    const cost  = num(t.costRate);
    const bench = num(t.benchmarkRate);
    gain       += (exitRate - cost)  * fc;
    exportGain += (bench    - cost)  * fc;
    holding    += (exitRate - bench) * fc;
  });
  return { gain, exportGain, holding };
};

// --------------------------------------------------------------------
const noteStyle = { fontSize: '12px', color: '#64748B' };

const warnBox = {
  background: '#FEF9C3', border: '1px solid #FDE68A',
  borderRadius: '6px', padding: '8px 12px',
  fontSize: '12px', color: '#92400E', marginBottom: '10px'
};

const infoBox = {
  background: '#F0F9FF', border: '1px solid #BAE6FD',
  borderRadius: '6px', padding: '10px 14px',
  fontSize: '13px', marginBottom: '14px'
};

const smallInput = {
  width: '110px', padding: '4px 8px',
  border: '1px solid #CBD5E1',
  borderRadius: '4px', fontSize: '13px'
};

const cell      = { padding: '7px 10px' };
const rightCell = { ...cell, textAlign: 'right' };

const gainColour = (v) =>
  num(v) > 0 ? '#166534' : num(v) < 0 ? '#B91C1C' : '#64748B';

const emptyForm = {
  mode: 'INR',
  conversionDate: today(),
  // to INR
  spotFc: '', spotRate: '',
  // cross currency
  targetAccountId: '', fcOut: '', crossRate: '', fcIn: '',
  sourceInrRate: '', targetInrRate: '',
  reference: '', notes: ''
};

function EefcPage({ token }) {
  const auth = { headers: { authorization: `Bearer ${token}` } };

  const [ledgers, setLedgers]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selected, setSelected]   = useState(null);   // accountId
  const [context, setContext]     = useState(null);
  const [ctxBusy, setCtxBusy]     = useState(false);

  const [showForm, setShowForm]   = useState(false);
  const [form, setForm]           = useState(emptyForm);
  const [saving, setSaving]       = useState(false);

  const [forwards, setForwards]           = useState([]);
  const [forwardReason, setForwardReason] = useState(null);
  const [forwardAllocations, setForwardAllocations] = useState([]);

  const [conversions, setConversions] = useState([]);
  const [openDetail, setOpenDetail]   = useState(null);
  const [detail, setDetail]           = useState(null);
  const [detailBusy, setDetailBusy]   = useState(false);

  useEffect(() => {
    loadLedgers();
    loadConversions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------------------------------------------------------
  // loaders
  // ------------------------------------------------------------------
  const loadLedgers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${CONFIG.API}/api/bills/eefc/ledgers`, auth);
      setLedgers(res.data || []);
    } catch (err) {
      console.error(err);
      setLedgers([]);
    } finally {
      setLoading(false);
    }
  };

  const loadConversions = async () => {
    try {
      const res = await axios.get(`${CONFIG.API}/api/bills/eefc/conversions`, auth);
      setConversions(res.data || []);
    } catch (err) {
      console.error(err);
      setConversions([]);
    }
  };

  const loadContext = async (accountId, date) => {
    if (!accountId) { setContext(null); return; }
    try {
      setCtxBusy(true);
      const res = await axios.get(
        `${CONFIG.API}/api/bills/eefc/context/${accountId}`,
        { ...auth, params: { date: date || '' } });
      setContext(res.data);
    } catch (err) {
      console.error(err);
      setContext(null);
    } finally {
      setCtxBusy(false);
    }
  };

  const loadForwards = async (accountId, date) => {
    if (!accountId || !date) {
      setForwards([]);
      setForwardReason('Enter the conversion date first — a forward\'s window has to cover it.');
      return;
    }
    try {
      const res = await axios.get(
        `${CONFIG.API}/api/bills/eefc/available-forwards/${accountId}`,
        { ...auth, params: { date } });
      const { list, reason } = readForwards(res.data);
      setForwards(list);
      setForwardReason(reason);
    } catch (err) {
      console.error(err);
      setForwards([]);
      setForwardReason(serverMessage(err, 'Could not load forward contracts.'));
    }
  };

  // ------------------------------------------------------------------
  // choosing a ledger
  // ------------------------------------------------------------------
  const selectLedger = async (acct) => {
    if (selected === acct.accountId) {
      setSelected(null);
      setContext(null);
      setShowForm(false);
      return;
    }
    setSelected(acct.accountId);
    setShowForm(false);
    setForm({ ...emptyForm, conversionDate: today() });
    setForwardAllocations([]);
    setForwards([]);
    setForwardReason(null);
    await loadContext(acct.accountId, today());
  };

  const startConversion = async () => {
    if (!selected) return;
    const next = { ...emptyForm, conversionDate: form.conversionDate || today() };
    setForm(next);
    setForwardAllocations([]);
    setShowForm(true);
    await loadForwards(selected, next.conversionDate);
    window.scrollTo(0, 0);
  };

  const closeForm = () => {
    setShowForm(false);
    setForm({ ...emptyForm, conversionDate: today() });
    setForwardAllocations([]);
    setForwards([]);
    setForwardReason(null);
  };

  // ------------------------------------------------------------------
  // form handling
  //
  // The cross rate and the amount received are kept in step with each
  // other: change one and the other is recalculated. Both are stored,
  // because the bank quotes a rate and credits an amount, and the two
  // do not always agree to the last decimal.
  // ------------------------------------------------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    let next = { ...form, [name]: value };

    if (name === 'mode') {
      next = {
        ...emptyForm,
        mode: value,
        conversionDate: form.conversionDate
      };
      setForm(next);
      setForwardAllocations([]);
      if (value === 'INR') loadForwards(selected, next.conversionDate);
      else { setForwards([]); setForwardReason(null); }
      return;
    }

    if (name === 'conversionDate') {
      setForm(next);
      setForwardAllocations([]);
      loadContext(selected, value);
      if (next.mode === 'INR') loadForwards(selected, value);
      return;
    }

    if (name === 'crossRate') {
      const out = num(next.fcOut);
      const cr  = num(value);
      if (out > 0 && cr > 0) next.fcIn = String(Number((out * cr).toFixed(2)));
      setForm(next);
      return;
    }

    if (name === 'fcIn') {
      const out = num(next.fcOut);
      const inn = num(value);
      if (out > 0 && inn > 0) next.crossRate = String(Number((inn / out).toFixed(6)));
      setForm(next);
      return;
    }

    if (name === 'fcOut') {
      const out = num(value);
      const cr  = num(next.crossRate);
      if (out > 0 && cr > 0) next.fcIn = String(Number((out * cr).toFixed(2)));
      setForm(next);
      return;
    }

    setForm(next);
  };

  const handleForwardAllocationChange = (forwardId, value) => {
    const existing = forwardAllocations.find(a => a.forwardId === forwardId);
    if (existing) {
      setForwardAllocations(forwardAllocations.map(a =>
        a.forwardId === forwardId ? { ...a, fcAllocated: value } : a));
    } else {
      const fwd = forwards.find(f => f.forwardId === forwardId);
      setForwardAllocations([...forwardAllocations, {
        forwardId, fcAllocated: value, forwardRate: fwd?.forwardRate || 0
      }]);
    }
  };

  // ------------------------------------------------------------------
  // derived — what this conversion would do
  // ------------------------------------------------------------------
  const parcels = context?.parcels || [];
  const balance = num(context?.balanceFc);

  const target = (context?.targets || []).find(
    t => String(t.accountId) === String(form.targetAccountId));

  const forwardFc = forwardAllocations
    .reduce((s, a) => s + num(a.fcAllocated), 0);

  const spotFc   = num(form.spotFc);
  const spotRate = num(form.spotRate);

  // The source market rate: from rate history, or typed when there is
  // none for that day.
  const sourceMarket = num(context?.marketRate) > 0
    ? num(context.marketRate) : num(form.sourceInrRate);
  const targetMarket = num(target?.marketRate) > 0
    ? num(target.marketRate) : num(form.targetInrRate);

  let fcToConvert = 0;
  let exitRate    = 0;
  let inrAmount   = 0;

  if (form.mode === 'INR') {
    fcToConvert = spotFc + forwardFc;
    let weighted = 0;
    if (spotFc > 0 && spotRate > 0) weighted += spotFc * spotRate;
    forwardAllocations.forEach(a => {
      const fc = num(a.fcAllocated);
      if (fc > 0) weighted += fc * num(a.forwardRate);
    });
    exitRate  = fcToConvert > 0 ? weighted / fcToConvert : 0;
    inrAmount = weighted;
  } else {
    fcToConvert = num(form.fcOut);
    exitRate    = sourceMarket;
    inrAmount   = fcToConvert * sourceMarket;
  }

  const plan   = planFifo(parcels, fcToConvert);
  const totals = gainsFor(plan.takes, exitRate);

  // Cross currency arithmetic
  const fcIn          = num(form.fcIn);
  const inrReceived   = fcIn * targetMarket;
  const bankCost      = inrAmount - inrReceived;
  const actualCross   = fcToConvert > 0 && fcIn > 0 ? fcIn / fcToConvert : 0;
  const impliedCross  = num(target?.impliedCross);

  const overBalance = fcToConvert > balance + TOL;

  // ------------------------------------------------------------------
  // save
  // ------------------------------------------------------------------
  const handleSubmit = async () => {
    if (!selected)            { alert('Choose an EEFC ledger first.'); return; }
    if (!form.conversionDate) { alert('Enter the conversion date.'); return; }

    if (form.mode === 'INR') {
      if (fcToConvert <= 0) {
        alert('Enter a spot amount, a forward allocation, or both.'); return;
      }
      if (spotFc > 0 && spotRate <= 0) {
        alert('Enter the spot rate for the amount being sold at spot.'); return;
      }
      if (spotFc < 0 || forwardAllocations.some(a => num(a.fcAllocated) < 0)) {
        alert('Amounts cannot be negative.'); return;
      }
    } else {
      if (!form.targetAccountId) {
        alert('Choose the currency this is being converted into.'); return;
      }
      if (fcToConvert <= 0) { alert('Enter the amount to convert.'); return; }
      if (num(form.crossRate) <= 0) {
        alert('Enter the cross rate the bank quoted.'); return;
      }
      if (fcIn <= 0) {
        alert('Enter the amount of the new currency the bank credited.'); return;
      }
      if (sourceMarket <= 0 || targetMarket <= 0) {
        alert('The market rates for both currencies are needed to value this '
            + 'conversion. Upload them on the Rates page, or type them in below.');
        return;
      }
    }

    if (overBalance) {
      alert(`This ledger holds ${fmt(balance)}, so ${fmt(fcToConvert)} cannot be `
          + 'converted from it.');
      return;
    }

    if (!plan.ok) {
      alert(plan.reason || 'The parcels in this ledger do not cover that amount.');
      return;
    }

    try {
      setSaving(true);

      if (form.mode === 'INR') {
        await axios.post(`${CONFIG.API}/api/bills/eefc/convert`, {
          accountId:      selected,
          conversionDate: form.conversionDate,
          spotFc:         spotFc   > 0 ? spotFc   : null,
          spotRate:       spotRate > 0 ? spotRate : null,
          reference:      form.reference || null,
          notes:          form.notes || null,
          forwardAllocations: forwardAllocations
            .filter(a => num(a.fcAllocated) > 0)
            .map(a => ({ forwardId: a.forwardId, fcAllocated: num(a.fcAllocated) }))
        }, auth);
      } else {
        await axios.post(`${CONFIG.API}/api/bills/eefc/convert-cross`, {
          accountId:       selected,
          targetAccountId: parseInt(form.targetAccountId),
          conversionDate:  form.conversionDate,
          fcOut:           fcToConvert,
          crossRate:       num(form.crossRate),
          fcIn:            fcIn,
          sourceInrRate:   num(form.sourceInrRate) > 0 ? num(form.sourceInrRate) : null,
          targetInrRate:   num(form.targetInrRate) > 0 ? num(form.targetInrRate) : null,
          reference:       form.reference || null,
          notes:           form.notes || null
        }, auth);
      }

      closeForm();
      await loadLedgers();
      await loadConversions();
      await loadContext(selected, form.conversionDate);
    } catch (err) {
      alert(serverMessage(err, 'Could not save the conversion.'));
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------------------------------------------
  // conversion detail + undo
  // ------------------------------------------------------------------
  const toggleDetail = async (txnId) => {
    if (openDetail === txnId) { setOpenDetail(null); setDetail(null); return; }
    try {
      setOpenDetail(txnId);
      setDetailBusy(true);
      const res = await axios.get(
        `${CONFIG.API}/api/bills/eefc/conversions/${txnId}/detail`, auth);
      setDetail(res.data);
    } catch (err) {
      console.error(err);
      setDetail(null);
    } finally {
      setDetailBusy(false);
    }
  };

  const handleUndo = async (c) => {
    if (!window.confirm(
      `Undo this conversion of ${fmt(c.fcAmount)} ${ccyCode(c.currency)} `
    + `at ${c.bankName}?\n\n`
    + 'The parcels it used get their balance back, any forward contract is '
    + 'released, and the gain recorded against it is removed.'
    )) return;

    try {
      await axios.delete(
        `${CONFIG.API}/api/bills/eefc/conversions/${c.txnId}`, auth);
      setOpenDetail(null);
      setDetail(null);
      await loadLedgers();
      await loadConversions();
      if (selected) await loadContext(selected, form.conversionDate);
    } catch (err) {
      alert(serverMessage(err, 'Could not undo this conversion.'));
    }
  };

  // ------------------------------------------------------------------
  // page-level figures
  // ------------------------------------------------------------------
  const byCurrency = {};
  ledgers.forEach(l => {
    if (num(l.balanceFc) > TOL) {
      byCurrency[l.currency] = (byCurrency[l.currency] || 0) + num(l.balanceFc);
    }
  });
  const currencies = Object.keys(byCurrency).sort();

  const totalGain    = conversions.reduce((s, c) => s + num(c.gainInr), 0);
  const totalHolding = conversions.reduce((s, c) => s + num(c.holdingGainInr), 0);
  const totalBankCost = conversions.reduce((s, c) => s + num(c.bankCostInr), 0);

  const selectedLedger = ledgers.find(l => l.accountId === selected);

  return (
    <div className="content">

      {/* ============================================================ */}
      {/* Summary cards                                                */}
      {/* ============================================================ */}
      <div className="metric-grid">
        <div className="metric-card">
          <div className="metric-label">Ledgers holding money</div>
          <div className="metric-value val-blue">{ledgers.length}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Conversions</div>
          <div className="metric-value val-blue">{conversions.length}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Gain realised (₹)</div>
          <div className="metric-value" style={{ color: gainColour(totalGain) }}>
            {conversions.length ? fmt0(totalGain) : '—'}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Of that, from holding (₹)</div>
          <div className="metric-value" style={{ color: gainColour(totalHolding) }}>
            {conversions.length ? fmt0(totalHolding) : '—'}
          </div>
        </div>
      </div>

      {/* EEFC held, per currency */}
      {currencies.length > 0 && (
        <div style={{
          background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: '8px',
          padding: '12px 16px', marginBottom: '16px',
          display: 'flex', gap: '28px', flexWrap: 'wrap', alignItems: 'center'
        }}>
          <span style={{ fontSize: '13px', fontWeight: '600', color: '#0C4A6E' }}>
            Held in EEFC
          </span>
          {currencies.map(c => (
            <span key={c} style={{ fontSize: '13px' }}>
              <strong>{pairLabel(c)}</strong> {fmt(byCurrency[c])} {ccyCode(c)}
            </span>
          ))}
          {totalBankCost !== 0 && (
            <span style={{ fontSize: '13px', marginLeft: 'auto', color: '#B91C1C' }}>
              <strong>Bank spread paid:</strong> ₹{fmt0(totalBankCost)}
            </span>
          )}
        </div>
      )}

      {/* ============================================================ */}
      {/* Ledgers                                                      */}
      {/* ============================================================ */}
      <div className="section-header">
        <div className="section-title">EEFC ledgers</div>
      </div>

      <div style={{ ...noteStyle, marginBottom: '14px' }}>
        Pick a ledger to see what it holds and convert from it. Ledgers are created and
        closed on the Bank page; this page is only about money moving out. Nothing here
        changes an invoice — the bill was settled when the money was parked.
      </div>

      {loading ? (
        <p style={{ padding: '20px', color: '#64748B' }}>Loading...</p>
      ) : ledgers.length === 0 ? (
        <div style={warnBox}>
          No EEFC ledger is holding money at the moment. Money arrives here when you keep
          part of a discounting or a realisation in EEFC instead of converting it.
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap',
                      marginBottom: '18px' }}>
          {ledgers.map(l => {
            const isOn = selected === l.accountId;
            return (
              <div key={l.accountId}
                onClick={() => selectLedger(l)}
                style={{
                  border: `1px solid ${isOn ? '#1F4E79' : '#E2E8F0'}`,
                  boxShadow: isOn ? '0 0 0 2px rgba(31,78,121,0.15)' : 'none',
                  borderRadius: '8px', padding: '12px 14px',
                  minWidth: '250px', cursor: 'pointer',
                  background: isOn ? '#F8FAFC' : '#FFFFFF'
                }}>
                <div style={{ display: 'flex', justifyContent: 'space-between',
                              alignItems: 'center', marginBottom: '6px' }}>
                  <span style={{ fontSize: '14px', fontWeight: '700', color: '#1F4E79' }}>
                    {pairLabel(l.currency)}
                  </span>
                  <span style={{ ...noteStyle }}>{l.bankName}</span>
                </div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#166534' }}>
                  {fmt(l.balanceFc)}{' '}
                  <span style={{ fontSize: '13px' }}>{ccyCode(l.currency)}</span>
                </div>
                <div style={noteStyle}>
                  Average cost {rate4(l.avgCostRate)}
                  {l.openLots ? ` · ${l.openLots} parcel${l.openLots > 1 ? 's' : ''}` : ''}
                  {l.oldestLotDate
                    ? ` · oldest ${String(l.oldestLotDate).slice(0, 10)}` : ''}
                </div>
                <div style={{ ...noteStyle, marginTop: '6px',
                              color: isOn ? '#1F4E79' : '#94A3B8', fontWeight: '600' }}>
                  {isOn ? '▲ Selected' : '▼ Select'}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ============================================================ */}
      {/* The selected ledger                                          */}
      {/* ============================================================ */}
      {selected && (
        <div style={{
          border: '1px solid #BAE6FD', borderRadius: '8px',
          background: '#F8FAFC', padding: '14px 16px', marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between',
                        alignItems: 'center', marginBottom: '12px' }}>
            <div style={{ fontSize: '14px', fontWeight: '700', color: '#1E293B' }}>
              {context?.bankName || selectedLedger?.bankName}
              {' — '}
              {pairLabel(context?.currency || selectedLedger?.currency)}
              <span style={{ ...noteStyle, fontWeight: '400', marginLeft: '10px' }}>
                balance {fmt(balance)} {ccyCode(context?.currency)}
                {num(context?.marketRate) > 0
                  ? ` · market ${rate4(context.marketRate)} (close of ${context.marketDate})`
                  : ' · no market rate on file'}
              </span>
            </div>
            {!showForm && (
              <button className="btn-primary" onClick={startConversion}>
                + Convert from this ledger
              </button>
            )}
          </div>

          {/* ---- parcels, oldest first ---- */}
          {ctxBusy ? (
            <div style={noteStyle}>Loading...</div>
          ) : parcels.length === 0 ? (
            <div style={noteStyle}>This ledger has no money left in it.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Arrived</th>
                    <th>Came from</th>
                    <th>Invoice</th>
                    <th>Customer</th>
                    <th style={{ textAlign: 'right' }}>Remaining</th>
                    <th style={{ textAlign: 'right' }}>Cost rate</th>
                    <th style={{ textAlign: 'right' }}>Rate on arrival</th>
                  </tr>
                </thead>
                <tbody>
                  {parcels.map((p, i) => (
                    <tr key={p.lotId}>
                      <td style={cell}>
                        {i === 0 && (
                          <span style={{ color: '#1F4E79', fontWeight: '700',
                                         marginRight: '6px' }}>1st out</span>
                        )}
                        {p.lotDate}
                      </td>
                      <td style={cell}>{p.sourceType}</td>
                      <td style={cell}>{p.invoiceNo || '—'}</td>
                      <td style={cell}>{p.customerCode || '—'}</td>
                      <td style={{ ...rightCell, fontWeight: '600' }}>
                        {fmt(p.fcRemaining)}
                      </td>
                      <td style={rightCell}>{rate4(p.costRate)}</td>
                      <td style={rightCell}>{rate4(p.benchmarkRate)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div style={{ ...noteStyle, marginTop: '8px' }}>
                Oldest first — this is the order money leaves in, and it is not a choice.
                Picking the parcel would mean picking which invoice takes the profit.
              </div>
            </div>
          )}
        </div>
      )}

      {/* ============================================================ */}
      {/* Conversion form                                              */}
      {/* ============================================================ */}
      {showForm && selected && (
        <div className="form-card" style={{ maxWidth: '900px' }}>
          <div className="form-step" style={{ marginBottom: '16px' }}>
            💱 Convert from {context?.bankName} — {pairLabel(context?.currency)}
          </div>

          {/* Row 1 — mode + date */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                        gap: '12px', marginBottom: '12px' }}>
            <div className="form-field">
              <label>Convert into</label>
              <select name="mode" value={form.mode} onChange={handleChange}>
                <option value="INR">Rupees (INR)</option>
                <option value="CROSS">Another currency (cross)</option>
              </select>
            </div>
            <div className="form-field">
              <label>Conversion date</label>
              <input type="date" name="conversionDate"
                value={form.conversionDate} onChange={handleChange} />
            </div>
            <div className="form-field">
              <label>Bank reference (optional)</label>
              <input name="reference" value={form.reference}
                onChange={handleChange} placeholder="Advice or deal reference" />
            </div>
          </div>

          {/* ======================================================== */}
          {/* TO INR                                                   */}
          {/* ======================================================== */}
          {form.mode === 'INR' && (
            <>
              <div style={{ fontSize: '13px', fontWeight: '600',
                            color: '#374151', marginBottom: '10px' }}>
                Sell for rupees — Spot / Forward
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                            gap: '12px', marginBottom: '6px' }}>
                <div className="form-field">
                  <label>Spot — FC amount</label>
                  <input type="number" min="0" name="spotFc" value={form.spotFc}
                    onChange={handleChange} placeholder="0" />
                </div>
                <div className="form-field">
                  <label>Spot rate</label>
                  <input type="number" min="0" name="spotRate" value={form.spotRate}
                    onChange={handleChange} placeholder="e.g. 84.50" />
                </div>
              </div>
              {spotFc > 0 && spotRate > 0 && (
                <div style={{ ...noteStyle, marginBottom: '8px', paddingLeft: '4px' }}>
                  Spot INR = {fmt0(spotFc * spotRate)}
                </div>
              )}

              {/* Forward table */}
              <div style={{ marginBottom: '10px' }}>
                <div style={{ fontSize: '12px', fontWeight: '600',
                              color: '#374151', marginBottom: '6px' }}>
                  Forward contracts at {context?.bankName} covering {form.conversionDate}
                </div>

                {forwards.length === 0 ? (
                  <div style={warnBox}>
                    {forwardReason || 'No forward contracts match.'}
                  </div>
                ) : (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Deal no</th>
                        <th>Bank</th>
                        <th>Forward rate</th>
                        <th>FC available</th>
                        <th>Window</th>
                        <th>Allocate FC</th>
                        <th>INR value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {forwards.map(f => {
                        const alloc   = forwardAllocations.find(
                          a => a.forwardId === f.forwardId);
                        const allocFc = num(alloc?.fcAllocated);
                        return (
                          <tr key={f.forwardId}>
                            <td>{f.dealNo}</td>
                            <td>{f.bank}</td>
                            <td>{rate4(f.forwardRate)}</td>
                            <td>{fmt(f.fcAvailable)}</td>
                            <td style={{ fontSize: '11px' }}>
                              {f.windowStartDate} to {f.windowEndDate}
                            </td>
                            <td>
                              <input type="number" min="0"
                                value={alloc?.fcAllocated || ''}
                                onChange={e => handleForwardAllocationChange(
                                  f.forwardId, e.target.value)}
                                placeholder="0" max={f.fcAvailable}
                                style={smallInput} />
                            </td>
                            <td style={{ color: '#166534', fontWeight: '600' }}>
                              {allocFc > 0
                                ? fmt0(allocFc * num(f.forwardRate)) : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}

          {/* ======================================================== */}
          {/* CROSS CURRENCY                                           */}
          {/* ======================================================== */}
          {form.mode === 'CROSS' && (
            <>
              <div style={{ fontSize: '13px', fontWeight: '600',
                            color: '#374151', marginBottom: '4px' }}>
                Buy another currency at the same bank
              </div>
              <div style={{ ...noteStyle, marginBottom: '10px' }}>
                The bank debits one of its accounts and credits another of its own, so both
                ledgers belong to {context?.bankName}. No forward contract can be used here —
                a forward locks a rate against the rupee, and this trade never touches it.
              </div>

              {(context?.targets || []).length === 0 ? (
                <div style={warnBox}>
                  {context?.bankName} has no other EEFC ledger to convert into. Add one for
                  the currency you want on the Bank page first.
                </div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                                gap: '12px', marginBottom: '12px' }}>
                    <div className="form-field">
                      <label>{ccyCode(context?.currency)} to convert</label>
                      <input type="number" min="0" name="fcOut" value={form.fcOut}
                        onChange={handleChange} placeholder="0" max={balance} />
                    </div>
                    <div className="form-field">
                      <label>Into which currency</label>
                      <select name="targetAccountId" value={form.targetAccountId}
                        onChange={handleChange}>
                        <option value="">— Select currency —</option>
                        {(context?.targets || []).map(t => (
                          <option key={t.accountId} value={t.accountId}>
                            {pairLabel(t.currency)}
                            {t.accountNo ? ` (${t.accountNo})` : ''}
                            {' — holding '}{fmt(t.balanceFc)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {target && (
                    <>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                                    gap: '12px', marginBottom: '6px' }}>
                        <div className="form-field">
                          <label>
                            Cross rate — 1 {ccyCode(context?.currency)} = ?{' '}
                            {ccyCode(target.currency)}
                          </label>
                          <input type="number" min="0" name="crossRate"
                            value={form.crossRate} onChange={handleChange}
                            placeholder="e.g. 1.1400" />
                        </div>
                        <div className="form-field">
                          <label>{ccyCode(target.currency)} the bank credited</label>
                          <input type="number" min="0" name="fcIn"
                            value={form.fcIn} onChange={handleChange}
                            placeholder="0" />
                        </div>
                      </div>
                      <div style={{ ...noteStyle, marginBottom: '12px' }}>
                        Change either one and the other follows. Both are recorded — the bank
                        quotes a rate and credits an amount, and they do not always agree to
                        the last decimal. If they differ, the amount is what counts.
                      </div>

                      {/* Market rates + the bank's spread */}
                      <div style={infoBox}>
                        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                          <span>
                            <strong>{pairLabel(context?.currency)} market:</strong>{' '}
                            {sourceMarket > 0
                              ? `${rate4(sourceMarket)}${context?.marketDate
                                  ? ` (close of ${context.marketDate})` : ''}`
                              : 'not available'}
                          </span>
                          <span>
                            <strong>{pairLabel(target.currency)} market:</strong>{' '}
                            {targetMarket > 0
                              ? `${rate4(targetMarket)}${target.marketDate
                                  ? ` (close of ${target.marketDate})` : ''}`
                              : 'not available'}
                          </span>
                          {impliedCross > 0 && (
                            <span>
                              <strong>Market cross:</strong> {rate4(impliedCross)}
                            </span>
                          )}
                          {actualCross > 0 && (
                            <span style={{
                              fontWeight: '600',
                              color: impliedCross > 0
                                ? (actualCross >= impliedCross ? '#166534' : '#B91C1C')
                                : '#1D4ED8'
                            }}>
                              <strong>Bank gave:</strong> {rate4(actualCross)}
                            </span>
                          )}
                        </div>

                        {fcToConvert > 0 && fcIn > 0 &&
                         sourceMarket > 0 && targetMarket > 0 && (
                          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap',
                                        marginTop: '8px' }}>
                            <span><strong>INR given up:</strong> {fmt0(inrAmount)}</span>
                            <span><strong>INR received:</strong> {fmt0(inrReceived)}</span>
                            <span style={{
                              color: bankCost > 0 ? '#B91C1C' : '#166534',
                              fontWeight: '700'
                            }}>
                              <strong>Bank's spread:</strong> ₹{fmt0(bankCost)}
                            </span>
                          </div>
                        )}

                        <div style={{ ...noteStyle, marginTop: '6px' }}>
                          The market rates are not part of the trade — they value it, because
                          every gain here is a rupee figure. The bank's spread is shown on its
                          own: an unfavourable cross rate is a cost of dealing, not a currency
                          movement.
                        </div>
                      </div>

                      {/* Manual market rates, only when history has none */}
                      {(num(context?.marketRate) === 0 || num(target.marketRate) === 0) && (
                        <>
                          <div style={warnBox}>
                            There is no market rate on file for{' '}
                            {num(context?.marketRate) === 0
                              ? pairLabel(context?.currency) : ''}
                            {num(context?.marketRate) === 0 && num(target.marketRate) === 0
                              ? ' and ' : ''}
                            {num(target.marketRate) === 0
                              ? pairLabel(target.currency) : ''}
                            {' '}on or before {form.conversionDate}. Upload it on the Rates
                            page, or type it below.
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                                        gap: '12px', marginBottom: '12px' }}>
                            {num(context?.marketRate) === 0 && (
                              <div className="form-field">
                                <label>{pairLabel(context?.currency)} market rate</label>
                                <input type="number" min="0" name="sourceInrRate"
                                  value={form.sourceInrRate} onChange={handleChange}
                                  placeholder="e.g. 95.20" />
                              </div>
                            )}
                            {num(target.marketRate) === 0 && (
                              <div className="form-field">
                                <label>{pairLabel(target.currency)} market rate</label>
                                <input type="number" min="0" name="targetInrRate"
                                  value={form.targetInrRate} onChange={handleChange}
                                  placeholder="e.g. 83.40" />
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </>
          )}

          {/* ======================================================== */}
          {/* What this would do — the FIFO preview                    */}
          {/* ======================================================== */}
          {fcToConvert > 0 && (
            <div style={{
              border: `1px solid ${overBalance || !plan.ok ? '#FCA5A5' : '#BBF7D0'}`,
              background: overBalance || !plan.ok ? '#FEF2F2' : '#F0FDF4',
              borderRadius: '6px', padding: '12px 14px',
              marginTop: '8px', marginBottom: '14px'
            }}>
              <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap',
                            fontSize: '13px', marginBottom: '8px' }}>
                <span>
                  <strong>Converting:</strong> {fmt(fcToConvert)} {ccyCode(context?.currency)}
                </span>
                <span><strong>Balance after:</strong> {fmt(balance - fcToConvert)}</span>
                {exitRate > 0 && (
                  <span style={{ color: '#1D4ED8', fontWeight: '600' }}>
                    <strong>Exit rate:</strong> {rate4(exitRate)}
                  </span>
                )}
                {exitRate > 0 && (
                  <span style={{ color: gainColour(totals.gain), fontWeight: '700' }}>
                    <strong>Gain:</strong> ₹{fmt0(totals.gain)}
                  </span>
                )}
                {exitRate > 0 && (
                  <span style={{ color: gainColour(totals.exportGain) }}>
                    <strong>Export:</strong> ₹{fmt0(totals.exportGain)}
                  </span>
                )}
                {exitRate > 0 && (
                  <span style={{ color: gainColour(totals.holding) }}>
                    <strong>Holding:</strong> ₹{fmt0(totals.holding)}
                  </span>
                )}
              </div>

              {(overBalance || !plan.ok) && (
                <div style={{ fontSize: '13px', color: '#B91C1C', fontWeight: '600' }}>
                  {overBalance
                    ? `This ledger holds ${fmt(balance)}, so ${fmt(fcToConvert)} cannot be converted from it.`
                    : plan.reason}
                </div>
              )}

              {plan.ok && plan.takes.length > 0 && (
                <div style={{ overflowX: 'auto' }}>
                  <div style={{ fontSize: '12px', fontWeight: '600',
                                color: '#374151', marginBottom: '6px' }}>
                    Parcels this will use — oldest first
                  </div>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Arrived</th>
                        <th>Invoice</th>
                        <th style={{ textAlign: 'right' }}>Taken</th>
                        <th style={{ textAlign: 'right' }}>Left after</th>
                        <th style={{ textAlign: 'right' }}>Cost</th>
                        <th style={{ textAlign: 'right' }}>On arrival</th>
                        <th style={{ textAlign: 'right' }}>Export ₹</th>
                        <th style={{ textAlign: 'right' }}>Holding ₹</th>
                        <th style={{ textAlign: 'right' }}>Gain ₹</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plan.takes.map(t => {
                        const fc    = num(t.fcTaken);
                        const cost  = num(t.costRate);
                        const bench = num(t.benchmarkRate);
                        const g = (exitRate - cost)  * fc;
                        const e = (bench    - cost)  * fc;
                        const h = (exitRate - bench) * fc;
                        return (
                          <tr key={t.lotId}>
                            <td style={cell}>{t.lotDate}</td>
                            <td style={cell}>{t.invoiceNo || '—'}</td>
                            <td style={{ ...rightCell, fontWeight: '600' }}>{fmt(fc)}</td>
                            <td style={rightCell}>{fmt(t.fcRemainingAfter)}</td>
                            <td style={rightCell}>{rate4(cost)}</td>
                            <td style={rightCell}>{rate4(bench)}</td>
                            <td style={{ ...rightCell, color: gainColour(e) }}>
                              {exitRate > 0 ? fmt0(e) : '—'}
                            </td>
                            <td style={{ ...rightCell, color: gainColour(h) }}>
                              {exitRate > 0 ? fmt0(h) : '—'}
                            </td>
                            <td style={{ ...rightCell, color: gainColour(g),
                                         fontWeight: '600' }}>
                              {exitRate > 0 ? fmt0(g) : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div style={{ ...noteStyle, marginTop: '8px' }}>
                    Export gain is what the export earned against the rate it was priced at.
                    Holding gain is what waiting earned on top. Nothing is saved yet — the
                    server works this out again when you save.
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Notes */}
          <div style={{ marginBottom: '16px' }}>
            <div className="form-field">
              <label>Notes (optional)</label>
              <textarea name="notes" value={form.notes} onChange={handleChange}
                rows={2} placeholder="Any additional notes..."
                style={{
                  width: '100%', padding: '8px 10px',
                  border: '1px solid #CBD5E1', borderRadius: '6px',
                  fontSize: '14px', resize: 'vertical',
                  fontFamily: 'inherit', boxSizing: 'border-box'
                }} />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button className="btn-secondary" onClick={closeForm}>Cancel</button>
            <button className="btn-primary" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : '✓ Save conversion'}
            </button>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* Conversions already made                                     */}
      {/* ============================================================ */}
      <div className="section-header">
        <div className="section-title">Conversions</div>
      </div>

      <div className="table-wrap" style={{ overflowX: 'auto' }}>
        {conversions.length === 0 ? (
          <p style={{ padding: '20px', color: '#64748B' }}>
            Nothing has been converted out of an EEFC ledger yet.
          </p>
        ) : (
          <table className="data-table" style={{ minWidth: '1250px' }}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Bank</th>
                <th>Currency</th>
                <th>Kind</th>
                <th style={{ textAlign: 'right' }}>FC out</th>
                <th style={{ textAlign: 'right' }}>Rate</th>
                <th>Into</th>
                <th style={{ textAlign: 'right' }}>INR</th>
                <th style={{ textAlign: 'right' }}>Export ₹</th>
                <th style={{ textAlign: 'right' }}>Holding ₹</th>
                <th style={{ textAlign: 'right' }}>Gain ₹</th>
                <th style={{ textAlign: 'right' }}>Bank cost ₹</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {conversions.map(c => (
                <React.Fragment key={c.txnId}>
                  <tr>
                    <td style={cell}>{c.txnDate}</td>
                    <td style={cell}>{c.bankName}</td>
                    <td style={cell}>{ccyCode(c.currency)}</td>
                    <td style={cell}>
                      <span className={`badge ${c.kind === 'CROSS'
                        ? 'badge-partial' : 'badge-open'}`}>
                        {c.kind === 'CROSS' ? 'CROSS' : 'INR'}
                      </span>
                    </td>
                    <td style={{ ...rightCell, fontWeight: '600' }}>{fmt(c.fcAmount)}</td>
                    <td style={rightCell}>
                      {c.kind === 'CROSS' ? rate4(c.crossRate) : rate4(c.exitRate)}
                    </td>
                    <td style={cell}>
                      {c.kind === 'CROSS'
                        ? `${fmt(c.counterFc)} ${ccyCode(c.counterCurrency)}`
                        : 'INR'}
                    </td>
                    <td style={rightCell}>{fmt0(c.inrAmount)}</td>
                    <td style={{ ...rightCell, color: gainColour(c.exportGainInr) }}>
                      {fmt0(c.exportGainInr)}
                    </td>
                    <td style={{ ...rightCell, color: gainColour(c.holdingGainInr) }}>
                      {fmt0(c.holdingGainInr)}
                    </td>
                    <td style={{ ...rightCell, color: gainColour(c.gainInr),
                                 fontWeight: '700' }}>
                      {fmt0(c.gainInr)}
                    </td>
                    <td style={{ ...rightCell, color: num(c.bankCostInr) > 0 ? '#B91C1C' : '#64748B' }}>
                      {num(c.bankCostInr) !== 0 ? fmt0(c.bankCostInr) : '—'}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn-secondary"
                        onClick={() => toggleDetail(c.txnId)}
                        style={{ marginRight: '6px' }}>
                        {openDetail === c.txnId ? '▲ Parcels' : '▼ Parcels'}
                      </button>
                      {c.canUndo ? (
                        <button className="btn-danger" onClick={() => handleUndo(c)}>
                          Undo
                        </button>
                      ) : (
                        <span title="Only the most recent movement on a ledger can be undone."
                          style={{ fontSize: '12px', color: '#92400E' }}>
                          🔒
                        </span>
                      )}
                    </td>
                  </tr>

                  {openDetail === c.txnId && (
                    <tr>
                      <td colSpan={13} style={{ background: '#F0F9FF', padding: '12px 14px' }}>
                        {detailBusy ? (
                          <div style={noteStyle}>Loading...</div>
                        ) : !detail ? (
                          <div style={noteStyle}>Could not load the detail.</div>
                        ) : (
                          <>
                            <div style={{ fontSize: '12px', fontWeight: '600',
                                          color: '#374151', marginBottom: '6px' }}>
                              Parcels this conversion used
                            </div>
                            <table className="data-table">
                              <thead>
                                <tr>
                                  <th>Arrived</th>
                                  <th>Came from</th>
                                  <th>Invoice</th>
                                  <th>Customer</th>
                                  <th style={{ textAlign: 'right' }}>Used</th>
                                  <th style={{ textAlign: 'right' }}>Cost</th>
                                  <th style={{ textAlign: 'right' }}>On arrival</th>
                                  <th style={{ textAlign: 'right' }}>Exit</th>
                                  <th style={{ textAlign: 'right' }}>Export ₹</th>
                                  <th style={{ textAlign: 'right' }}>Holding ₹</th>
                                  <th style={{ textAlign: 'right' }}>Gain ₹</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(detail.parcels || []).map(p => (
                                  <tr key={p.lotId}>
                                    <td style={cell}>{p.lotDate}</td>
                                    <td style={cell}>{p.sourceType}</td>
                                    <td style={cell}>{p.invoiceNo || '—'}</td>
                                    <td style={cell}>{p.customerCode || '—'}</td>
                                    <td style={{ ...rightCell, fontWeight: '600' }}>
                                      {fmt(p.fcUsed)}
                                    </td>
                                    <td style={rightCell}>{rate4(p.costRate)}</td>
                                    <td style={rightCell}>{rate4(p.benchmarkRate)}</td>
                                    <td style={rightCell}>{rate4(p.exitRate)}</td>
                                    <td style={{ ...rightCell, color: gainColour(p.exportGainInr) }}>
                                      {fmt0(p.exportGainInr)}
                                    </td>
                                    <td style={{ ...rightCell, color: gainColour(p.holdingGainInr) }}>
                                      {fmt0(p.holdingGainInr)}
                                    </td>
                                    <td style={{ ...rightCell, color: gainColour(p.gainInr),
                                                 fontWeight: '600' }}>
                                      {fmt0(p.gainInr)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>

                            {(detail.forwards || []).length > 0 && (
                              <div style={{ marginTop: '12px' }}>
                                <div style={{ fontSize: '12px', fontWeight: '600',
                                              color: '#374151', marginBottom: '6px' }}>
                                  Forward contracts used
                                </div>
                                <table className="data-table">
                                  <thead>
                                    <tr>
                                      <th>Deal no</th>
                                      <th>Bank</th>
                                      <th style={{ textAlign: 'right' }}>FC</th>
                                      <th style={{ textAlign: 'right' }}>Rate</th>
                                      <th>Window</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {detail.forwards.map(f => (
                                      <tr key={f.forwardId}>
                                        <td style={cell}>{f.dealNo}</td>
                                        <td style={cell}>{f.bank}</td>
                                        <td style={rightCell}>{fmt(f.fcAllocated)}</td>
                                        <td style={rightCell}>{rate4(f.forwardRate)}</td>
                                        <td style={{ ...cell, fontSize: '11px' }}>
                                          {f.windowStartDate} to {f.windowEndDate}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}

                            <div style={{ ...noteStyle, marginTop: '8px' }}>
                              Each row traces back to the export invoice the money came from.
                              This is what a report showing profit per invoice reads.
                            </div>
                          </>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {conversions.length > 0 && (
        <div style={{ ...noteStyle, marginTop: '10px' }}>
          A conversion is not a realisation — the bill was already settled when the money was
          parked. Nothing on this page changes an invoice balance. Undo is allowed only on the
          most recent movement on a ledger, because later ones were worked out from the
          balance it left behind.
        </div>
      )}

    </div>
  );
}

export default EefcPage;
